import React, { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { blockInfoGraphicType, flattenedProxyMapState, isReportState, vdparFundIdsState } from 'venn-state';
import type { ColDef } from 'ag-grid-community';

import { BaseGrid } from '../../components/visualizations/grids/base-grid';
import { useAppPrintMode } from '../../../print';
import { useCommonTreeGridProps } from '../../../studio-blocks/logic/columnUtils';
import ExportableGrid from '../../../studio-blocks/components/grid/ExportableGrid';
import { autoGroupColumnDefStaticConstants } from '../../../studio-blocks/components/grid/AgGridThemeOverrides';
import { treeGridColDefOverrides } from '../../utils';
import type { FPITreeRow } from '../../../studio-blocks/types';

import { useFinancialPerformanceIndicatorsData } from './logic/useFinancialPerformanceIndicatorsData';
import { useFinancialPerformanceIndicatorsTable } from './logic/useFinancialPerformanceIndicatorsTable';
import { useFinancialPerformanceIndicatorsTreeTable } from './logic/useFinancialPerformanceIndicatorsTreeTable';
import { usePortfolioMultiFiltering } from '../../utils/hooks/use-portfolio-multi-filtering';
import {
  renderSuperscripts,
  usePageProxySymbol,
  usePageVdparSymbol,
} from '../../components/core/block-subject-footnotes-and-superscripts';

const useFPIAutoGroupColumnDef = () => {
  const proxyMap = useRecoilValue(flattenedProxyMapState);
  const proxySymbol = usePageProxySymbol();
  const vdparFundIds = useRecoilValue(vdparFundIdsState);
  const vdparSymbol = usePageVdparSymbol();

  const cellRenderer = useCallback(
    ({ data }: { data: FPITreeRow }) => {
      if (!data.fundId) {
        return data.label;
      }
      return (
        <>
          {data.label}
          {renderSuperscripts([
            { condition: !!proxyMap?.[data.fundId], symbol: proxySymbol },
            { condition: vdparFundIds.has(data.fundId), symbol: vdparSymbol },
          ])}
        </>
      );
    },
    [proxyMap, proxySymbol, vdparFundIds, vdparSymbol],
  );

  return useMemo(
    (): ColDef => ({
      ...autoGroupColumnDefStaticConstants,
      cellRenderer,
    }),
    [cellRenderer],
  );
};

export const FinancialPerformanceIndicatorsContent = ({ id }: { id: string }) => {
  const infoGraphicType = useRecoilValue(blockInfoGraphicType(id));

  if (infoGraphicType === 'GRID') {
    return <Grid id={id} />;
  }

  if (infoGraphicType === 'PIVOTED_GRID') {
    return <PivotedGrid id={id} />;
  }

  return null;
};

const PivotedGrid = ({ id }: { id: string }) => {
  const { inPrintModeOrReportLab } = useAppPrintMode();
  const { hasMultiLevelPortfolioFiltering, filterRowData } = usePortfolioMultiFiltering(id);

  const data = useFinancialPerformanceIndicatorsData(id);
  const { rowData: rawRowData, columnDefs } = useFinancialPerformanceIndicatorsTreeTable(id, data);
  const rowData = useMemo(
    () => (hasMultiLevelPortfolioFiltering ? filterRowData(rawRowData) : rawRowData),
    [rawRowData, filterRowData, hasMultiLevelPortfolioFiltering],
  );

  const treeGridProps = useCommonTreeGridProps(treeGridColDefOverrides);
  const autoGroupColumnDef = useFPIAutoGroupColumnDef();

  return (
    <ExportableGrid
      {...treeGridProps}
      selectedRefId={id}
      inPrintMode={inPrintModeOrReportLab}
      rowData={rowData}
      columnDefs={columnDefs}
      exportable={false}
      isCompact={false}
      autoGroupColumnDef={autoGroupColumnDef}
    />
  );
};

const Grid = ({ id }: { id: string }) => {
  const { inPrintModeOrReportLab } = useAppPrintMode();
  const isReport = useRecoilValue(isReportState);

  const data = useFinancialPerformanceIndicatorsData(id);
  const { rowData, columnDefs } = useFinancialPerformanceIndicatorsTable(id, data);

  return (
    <BaseGrid
      selectedRefId={id}
      inPrintMode={inPrintModeOrReportLab}
      rowData={rowData}
      columnDefs={columnDefs}
      isReport={isReport}
      exportable={false}
      isCompact={false}
    />
  );
};
